import React, { createContext, useContext } from 'react'
import { Product } from '@/models/shop.ts'
import { useQuery } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import { useDrives } from '@/stores/useDrives.ts'

export interface ProductActions {
  refetch: () => Promise<any>
  getProduct(id: string): Product | undefined
  getPrintProducts(): Array<Product>
  getRegularProducts(): Array<Product>
}

interface ProductActionsContext {
  products: Array<Product>
  isLoading: boolean
}

export const ProductActionsContext = createContext<ProductActionsContext & ProductActions>({
  products: [],
  isLoading: false,
  getProduct(): Product | undefined {
    return undefined
  },
  getPrintProducts(): Array<Product> {
    return []
  },
  getRegularProducts(): Array<Product> {
    return []
  },
  async refetch() {},
})

export function ProductActionsProvider({ children }: { children: React.ReactNode }) {
  const { getProducts } = useClient()
  const { currentDrive } = useDrives()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const res = await getProducts(currentDrive!.id)
      return res.data
    },
  })

  function getProduct(id: string): Product | undefined {
    return (data || []).find((product: Product) => product.id === id)
  }

  function getPrintProducts(): Array<Product> {
    return (data || []).filter((product: Product) => product.is_print_product)
  }

  function getRegularProducts(): Array<Product> {
    return (data || []).filter((product: Product) => !product.is_print_product)
  }

  return <ProductActionsContext.Provider value={{
    products: data || [],
    getProduct,
    getPrintProducts,
    getRegularProducts,
    refetch,
    isLoading,
  }}>
    { children }
  </ProductActionsContext.Provider>
}

export function useProducts() {
  return useContext(ProductActionsContext)
}
